import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Autocomplete,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import useGetTags from "@/hooks/requests/tags/use-get-tags";
import { ITag } from "@/interfaces/tag.interface";
import { ContactInfoMessages } from "@/messages/contacts/contact-Info.messages";
import linearGradient from "@/themes/functions/linearGradient";
import { FilterType } from "@/types/filter.types";

import { ContactFormData } from "../../../../types";

type TagsProps = { messages: ContactInfoMessages };

export default function Tags({ messages }: TagsProps) {
  const { values, setFieldValue } = useFormikContext<ContactFormData>();
  const [tags, setTags] = useState<string[]>([]);
  const [search, setSearch] = useState<string | null>("");
  const [filteredTags, setFilteredTags] = useState<ITag[]>([]);
  const { showToast } = useToast();

  const theme = useTheme();
  const { gradients } = theme.palette;

  useEffect(() => {
    if (Array.isArray(values.tags)) setTags(values.tags);
  }, [values.tags]);

  const { data, isLoading } = useGetTags({
    pageNumber: 0,
    pageSize: 50,
    filters: [{ property: "name", filterType: FilterType.Contains, value: search || "" }],
    isPaused: !search,
  });

  useEffect(() => {
    if (data && data.items) setFilteredTags(data.items);
  }, [data]);

  const { addTagLabel, tagExistsError } = messages;

  const handleAddTag = (newTag: string) => {
    const trimmedTag = newTag.trim();

    if (trimmedTag !== "" && !tags.includes(trimmedTag)) {
      const updatedTags = [...tags, trimmedTag];
      setTags(updatedTags);
      setFieldValue("tags", updatedTags);

      if (
        !filteredTags.find((tag) => tag.name.toLocaleUpperCase() === trimmedTag.toLocaleUpperCase())
      ) {
        setFieldValue("newTags", [...values.newTags, trimmedTag]);
      }

      setSearch("");
    } else if (tags.includes(trimmedTag)) {
      showToast({ text: tagExistsError, type: "error" });
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToDelete);
    setTags(updatedTags);
    setFieldValue("tags", updatedTags);

    setFieldValue(
      "newTags",
      values.newTags.filter((tag) => tag !== tagToDelete)
    );
  };

  const handleInputKeyDown = () => {
    if (!search) return;

    const trimmedSearch = search.trim();

    if (trimmedSearch !== "" && !tags.includes(trimmedSearch)) {
      handleAddTag(trimmedSearch);
    } else if (tags.includes(trimmedSearch)) {
      showToast({ text: tagExistsError, type: "error" });
    }
  };

  const gradientColor = gradients["dark"];
  const chipBackGround = linearGradient(gradientColor?.main, gradientColor?.state);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.tagsSectionTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Autocomplete
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    paddingRight: "0px!important",
                  },
                }}
                freeSolo
                value={search || ""}
                onInputChange={(_event, newValue) => setSearch(newValue)}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                options={filteredTags}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={typeof option === "string" ? option : option.id || option.name}
                  >
                    {typeof option === "string" ? option : option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={addTagLabel}
                    fullWidth
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && search && search.trim()) {
                        e.preventDefault();
                        handleInputKeyDown();
                      }
                    }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => handleAddTag(search || "")}
                                disabled={typeof search !== "string" || search.trim() === ""}
                              >
                                <AddIcon />
                              </IconButton>
                            </InputAdornment>
                          </>
                        ),
                      },

                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12 }} sx={{ overflowY: "scroll" }}>
              {tags.map((tag, index) => (
                <Chip
                  key={`${index}-${tag}`}
                  variant="outlined"
                  label={tag}
                  sx={{
                    background: chipBackGround,
                    color: "white !important",
                    margin: "0 3px 3px 0",
                  }}
                  deleteIcon={<HighlightOffIcon fontSize="medium" style={{ color: "white" }} />}
                  onDelete={() => handleDeleteTag(tag)}
                />
              ))}
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
